import { useState, useCallback, useMemo, useRef, useEffect } from "react";

// ── LOCALSTORAGE HELPERS ──────────────────────────────────────────────────────
function lsGet(key, fallback) {
  try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}
function lsSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// ── DATA POOLS ────────────────────────────────────────────────────────────────
const FIRST = ["James","Maria","Oliver","Sophia","Liam","Emma","Noah","Ava","William","Isabella","Benjamin","Mia","Lucas","Charlotte","Henry","Amelia","Alexander","Harper","Mason","Evelyn","Ethan","Abigail","Daniel","Emily","Matthew","Elizabeth","Aiden","Mila","Sebastian","Ella","Michael","Luna","Logan","Sofia","Owen","Camila","Nathan","Aria","Ryan","Scarlett","Dylan","Victoria","Samuel","Grace","Gabriel","Chloe","Anthony","Penelope","Carter","Layla","Luke","Riley","Jayden","Zoey","David","Nora","Julian","Lily","Jack","Eleanor","Caleb","Hannah","Joseph","Lillian","Andrew","Addison","Aaron","Aubrey","Eli","Ellie","Thomas","Stella","Charles","Natalie","Christopher","Zoe","Joshua","Leah","Isaiah","Hazel","Hudson","Violet","Leonardo","Aurora","Mateo","Savannah","Asher","Audrey","Elijah","Brooklyn","John","Bella","Christian","Claire","Ezra","Skylar","Lincoln","Lucy"];
const LAST  = ["Smith","Johnson","Williams","Brown","Jones","Garcia","Miller","Davis","Rodriguez","Martinez","Hernandez","Lopez","Gonzalez","Wilson","Anderson","Thomas","Taylor","Moore","Jackson","Martin","Lee","Perez","Thompson","White","Harris","Sanchez","Clark","Ramirez","Lewis","Robinson","Walker","Young","Allen","King","Wright","Scott","Torres","Nguyen","Hill","Flores","Green","Adams","Nelson","Baker","Hall","Rivera","Campbell","Mitchell","Carter","Roberts","Gomez","Phillips","Evans","Turner","Diaz","Parker","Cruz","Edwards","Collins","Reyes","Stewart","Morris","Morales","Murphy","Cook","Rogers","Gutierrez","Ortiz","Morgan","Cooper","Peterson","Bailey","Reed","Kelly","Howard","Ramos","Kim","Cox","Ward","Richardson","Watson","Brooks","Chavez","Wood","James","Bennett","Gray","Mendoza","Ruiz","Hughes","Price","Alvarez","Castillo","Sanders","Patel","Myers","Long","Ross","Foster","Jimenez"];
const DOMAINS   = ["gmail.com","yahoo.com","outlook.com","hotmail.com","icloud.com","protonmail.com","mail.com","aol.com","live.com","zoho.com"];
const COMPANIES = ["Acme Corp","TechNova","BlueSky Inc","Vertex Labs","Quantum IO","NexGen Systems","Apex Digital","Stellar Works","CodeCraft","DataBridge","Horizon Tech","Pulse Media","Orbit Solutions","CloudStack","Nimbus AI","Ironclad LLC","Redwood Group","Cascade Systems","Meridian Labs","Vortex Co"];
const STREETS   = ["Main St","Oak Ave","Maple Dr","Cedar Ln","Pine Rd","Elm St","Birch Blvd","Walnut Way","Willow Ct","Spruce Pl","River Rd","Sunset Blvd","Park Ave","Lake Dr","Hill St","Valley Rd","Forest Ln","Meadow Dr","Summit Ave","Harbor Blvd"];
const CITIES    = ["New York","Los Angeles","Chicago","Houston","Phoenix","Philadelphia","San Antonio","San Diego","Dallas","San Jose","Austin","Jacksonville","Fort Worth","Columbus","Charlotte","Indianapolis","San Francisco","Seattle","Denver","Nashville","Oklahoma City","El Paso","Washington","Las Vegas","Louisville","Memphis","Portland","Baltimore","Milwaukee","Albuquerque","Tucson","Fresno","Sacramento","Mesa","Kansas City","Atlanta","Omaha","Colorado Springs","Raleigh","Miami"];
const STATES    = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const JOBS      = ["Software Engineer","Product Manager","Data Analyst","UX Designer","DevOps Engineer","QA Engineer","Frontend Developer","Backend Developer","Full Stack Developer","Data Scientist","Marketing Manager","Sales Representative","HR Manager","Financial Analyst","Project Manager","Systems Administrator","Network Engineer","Security Analyst","Content Writer","Business Analyst"];
const AVATARS   = ["😀","😊","🧑","👨","👩","🧔","👱","🧑‍💻","👨‍💻","👩‍💻","🧑‍🎨","👨‍🎤","👩‍🎤","🧑‍🔬","👨‍🏫","👩‍🏫","🧑‍💼","👨‍💼","👩‍💼","🧑‍🎓"];
const COUNTRIES = ["United States","United Kingdom","Canada","Australia","Germany","France","Japan","India","Brazil","Mexico","Italy","Spain","South Korea","Netherlands","Sweden","Norway","Denmark","Finland","Switzerland","Singapore"];
const COLORS    = ["red","blue","green","yellow","orange","purple","pink","teal","navy","maroon","coral","olive","cyan","magenta","indigo","lime","amber","slate","violet","rose"];
const DEPTS     = ["Engineering","Product","Design","Marketing","Sales","Finance","HR","Operations","Legal","Research","Support","IT","Data","Security","Infrastructure"];
const STATUSES  = ["active","inactive","pending","suspended","verified"];

// ── RNG ───────────────────────────────────────────────────────────────────────
const rnd      = arr => arr[Math.floor(Math.random() * arr.length)];
const rndInt   = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const rndFloat = (min, max, dec = 2) => parseFloat((Math.random() * (max - min) + min).toFixed(dec));
const rndBool  = () => Math.random() > 0.5;
const rndDate  = (startY = 2015, endY = 2024) => new Date(rndInt(startY, endY), rndInt(0, 11), rndInt(1, 28)).toISOString().split("T")[0];
const rndPhone = () => `+1 (${rndInt(200,999)}) ${rndInt(100,999)}-${rndInt(1000,9999)}`;
const rndZip   = () => String(rndInt(10000, 99999));
const rndId    = i  => `usr_${String(i + 1).padStart(4, "0")}`;
const rndEmail = (first, last) => `${first.toLowerCase()}.${last.toLowerCase()}${rndBool() ? rndInt(1,99) : ""}@${rnd(DOMAINS)}`;
const rndIp    = () => `${rndInt(1,254)}.${rndInt(0,255)}.${rndInt(0,255)}.${rndInt(1,254)}`;
const rndUrl   = co => `https://www.${co.toLowerCase().replace(/[^a-z]/g,"")}.com`;
const rndCC    = () => `4${Array.from({length:15},()=>rndInt(0,9)).join("")}`.replace(/(\d{4})(?=\d)/g,"$1-");
const rndPwd   = () => { const c="abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%"; return Array.from({length:rndInt(10,16)},()=>c[rndInt(0,c.length-1)]).join(""); };
const rndLorem = n => { const w=["lorem","ipsum","dolor","sit","amet","consectetur","adipiscing","elit","sed","do","eiusmod","tempor","incididunt","ut","labore","et","dolore","magna","aliqua","enim","ad","minim","veniam","quis","nostrud"]; return Array.from({length:n},()=>rnd(w)).join(" "); };
const rndUUID  = () => "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => { const r = (Math.random() * 16) | 0; return (c === "x" ? r : ((r & 0x3) | 0x8)).toString(16); });

// ── FIELD DEFINITIONS ─────────────────────────────────────────────────────────
const FIELD_GROUPS = [
  { label:"Identity", color:"blue", fields:[
    { id:"id",          label:"ID",            icon:"#",  gen: i  => rndId(i) },
    { id:"firstName",   label:"First Name",    icon:"👤", gen: () => rnd(FIRST) },
    { id:"lastName",    label:"Last Name",     icon:"👤", gen: () => rnd(LAST) },
    { id:"fullName",    label:"Full Name",     icon:"👤", gen: () => `${rnd(FIRST)} ${rnd(LAST)}` },
    { id:"email",       label:"Email",         icon:"✉",  gen: () => { const f=rnd(FIRST),l=rnd(LAST); return rndEmail(f,l); } },
    { id:"username",    label:"Username",      icon:"@",  gen: () => `${rnd(FIRST).toLowerCase()}${rndInt(10,999)}` },
    { id:"avatar",      label:"Avatar Emoji",  icon:"😊", gen: () => rnd(AVATARS) },
    { id:"age",         label:"Age",           icon:"🎂", gen: () => rndInt(18,65) },
    { id:"gender",      label:"Gender",        icon:"⚥",  gen: () => rnd(["male","female","non-binary"]) },
    { id:"dob",         label:"Date of Birth", icon:"📅", gen: () => rndDate(1960,2005) },
  ]},
  { label:"Contact", color:"green", fields:[
    { id:"phone",    label:"Phone",   icon:"📞", gen: () => rndPhone() },
    { id:"mobile",   label:"Mobile",  icon:"📱", gen: () => rndPhone() },
    { id:"website",  label:"Website", icon:"🌐", gen: () => rndUrl(rnd(COMPANIES)) },
    { id:"country",  label:"Country", icon:"🌍", gen: () => rnd(COUNTRIES) },
  ]},
  { label:"Address", color:"amber", fields:[
    { id:"street",      label:"Street",       icon:"🏠", gen: () => `${rndInt(1,9999)} ${rnd(STREETS)}` },
    { id:"city",        label:"City",         icon:"🏙", gen: () => rnd(CITIES) },
    { id:"state",       label:"State",        icon:"📍", gen: () => rnd(STATES) },
    { id:"zip",         label:"Zip Code",     icon:"🔢", gen: () => rndZip() },
    { id:"fullAddress", label:"Full Address", icon:"📮", gen: () => `${rndInt(1,9999)} ${rnd(STREETS)}, ${rnd(CITIES)}, ${rnd(STATES)} ${rndZip()}` },
  ]},
  { label:"Work", color:"blue", fields:[
    { id:"company",    label:"Company",    icon:"🏢", gen: () => rnd(COMPANIES) },
    { id:"jobTitle",   label:"Job Title",  icon:"💼", gen: () => rnd(JOBS) },
    { id:"department", label:"Department", icon:"🗂", gen: () => rnd(DEPTS) },
    { id:"salary",     label:"Salary",     icon:"💰", gen: () => rndInt(40000,180000) },
    { id:"startDate",  label:"Start Date", icon:"📅", gen: () => rndDate(2015,2024) },
  ]},
  { label:"System", color:"green", fields:[
    { id:"uuid",      label:"UUID",       icon:"🔑", gen: () => rndUUID() },
    { id:"ipAddress", label:"IP Address", icon:"🌐", gen: () => rndIp() },
    { id:"status",    label:"Status",     icon:"⚡", gen: () => rnd(STATUSES) },
    { id:"isActive",  label:"Is Active",  icon:"✓",  gen: () => rndBool() },
    { id:"createdAt", label:"Created At", icon:"🕐", gen: () => rndDate(2018,2024) },
    { id:"score",     label:"Score",      icon:"⭐", gen: () => rndFloat(0,100,1) },
    { id:"rating",    label:"Rating",     icon:"⭐", gen: () => rndFloat(1,5,1) },
    { id:"color",     label:"Fav Color",  icon:"🎨", gen: () => rnd(COLORS) },
  ]},
  { label:"Financial", color:"amber", fields:[
    { id:"creditCard", label:"Credit Card",  icon:"💳", gen: () => rndCC() },
    { id:"balance",    label:"Balance ($)",  icon:"💵", gen: () => rndFloat(0,50000,2) },
    { id:"currency",   label:"Currency",     icon:"💱", gen: () => rnd(["USD","EUR","GBP","JPY","CAD","AUD","CHF","INR","BRL","MXN"]) },
  ]},
  { label:"Misc", color:"blue", fields:[
    { id:"password", label:"Password", icon:"🔒", gen: () => rndPwd() },
    { id:"bio",      label:"Bio",      icon:"📝", gen: () => rndLorem(rndInt(8,18)) },
    { id:"notes",    label:"Notes",    icon:"📋", gen: () => rndLorem(rndInt(5,12)) },
  ]},
];

const ALL_FIELDS     = FIELD_GROUPS.flatMap(g => g.fields);
const DEFAULT_FIELDS = ["id","firstName","lastName","email","phone","company","jobTitle","city","country","status"];

// ── CUSTOM FIELD VALUE TYPES ──────────────────────────────────────────────────
const CUSTOM_TYPES = [
  { id:"static",    label:"Static Value",    hint:"Same value in every row" },
  { id:"list",      label:"Pick from List",  hint:"Comma-separated values, picked randomly" },
  { id:"number",    label:"Random Number",   hint:"Min and max range" },
  { id:"boolean",   label:"True / False",    hint:"Random true or false" },
  { id:"increment", label:"Incrementing #",  hint:"Starts at 1, goes up each row" },
  { id:"uuid",      label:"UUID",            hint:"Random UUID v4 per row" },
  { id:"date",      label:"Random Date",     hint:"Random date between two years" },
];

function generateCustomValue(field, rowIndex) {
  switch (field.type) {
    case "static":    return field.value || "";
    case "list":      return rnd((field.value || "").split(",").map(s => s.trim()).filter(Boolean)) ?? "";
    case "number":    return rndInt(Number(field.min ?? 0), Number(field.max ?? 100));
    case "boolean":   return rndBool();
    case "increment": return rowIndex + 1;
    case "uuid":      return rndUUID();
    case "date":      return rndDate(Number(field.startYear ?? 2020), Number(field.endYear ?? 2024));
    default:          return "";
  }
}

function generateRow(builtinIds, customFields, index) {
  const row = {};
  const first = rnd(FIRST), last = rnd(LAST);
  for (const id of builtinIds) {
    const f = ALL_FIELDS.find(f => f.id === id);
    if (!f) continue;
    if      (id === "firstName") row[id] = first;
    else if (id === "lastName")  row[id] = last;
    else if (id === "fullName")  row[id] = `${first} ${last}`;
    else if (id === "email")     row[id] = rndEmail(first, last);
    else                         row[id] = f.gen(index);
  }
  for (const cf of customFields) {
    if (cf.name.trim()) row[cf.name.trim()] = generateCustomValue(cf, index);
  }
  return row;
}

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit" });
}

function toCSV(rows, cols) {
  const esc = v => { const s = String(v ?? ""); return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g,'""')}"` : s; };
  return [cols.join(","), ...rows.map(r => cols.map(c => esc(r[c])).join(","))].join("\n");
}

const TABS = ["Generator", "History"];

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function FakeDataGenerator({ t, onExport }) {
  const [activeTab, setActiveTab]       = useState("Generator");
  const [selectedFields, setSelectedFields] = useState(new Set(DEFAULT_FIELDS));
  const [rowCount, setRowCount]         = useState(10);
  const [generatedData, setGeneratedData] = useState([]);
  const [outputFormat, setOutputFormat] = useState("table");
  const [copied, setCopied]             = useState(false);
  const [copiedCell, setCopiedCell]     = useState("");
  const [groupSearch, setGroupSearch]   = useState("");
  const [hasGenerated, setHasGenerated] = useState(false);
  const [history, setHistory]           = useState(() => lsGet("mdt-fake-history", []));
  const outputRef = useRef(null);

  // Scroll to output whenever new data is generated
  useEffect(() => {
    if (hasGenerated && generatedData.length > 0 && outputRef.current) {
      outputRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [generatedData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Custom fields state ──
  const [customFields, setCustomFields] = useState([]);
  const [customOpen, setCustomOpen]     = useState(true);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState("static");
  const [newFieldValue, setNewFieldValue]     = useState("");
  const [newFieldMin, setNewFieldMin]         = useState("0");
  const [newFieldMax, setNewFieldMax]         = useState("100");
  const [newFieldStartYear, setNewFieldStartYear] = useState("2020");
  const [newFieldEndYear, setNewFieldEndYear]     = useState("2024");

  const toggleField = id => setSelectedFields(prev => {
    const n = new Set(prev); n.has(id) ? (n.size > 1 && n.delete(id)) : n.add(id); return n;
  });

  const builtinIds = useMemo(() => ALL_FIELDS.map(f => f.id).filter(id => selectedFields.has(id)), [selectedFields]);
  const allColumnIds = useMemo(() => [...builtinIds, ...customFields.filter(f => f.name.trim()).map(f => f.name.trim())], [builtinIds, customFields]);

  const addCustomField = () => {
    if (!newFieldName.trim()) return;
    const exists = customFields.some(f => f.name.trim() === newFieldName.trim()) ||
                   ALL_FIELDS.some(f => f.id === newFieldName.trim());
    if (exists) { alert(`Field name "${newFieldName}" already exists`); return; }
    setCustomFields(prev => [...prev, {
      id: Date.now(),
      name: newFieldName.trim(),
      type: newFieldType,
      value: newFieldValue,
      min: newFieldMin,
      max: newFieldMax,
      startYear: newFieldStartYear,
      endYear: newFieldEndYear,
    }]);
    setNewFieldName(""); setNewFieldValue("");
  };

  const removeCustomField = id => setCustomFields(prev => prev.filter(f => f.id !== id));

  const generate = useCallback(() => {
    const count = Math.min(Math.max(1, rowCount), 1000);
    const rows  = Array.from({ length: count }, (_, i) => generateRow(builtinIds, customFields, i));
    setGeneratedData(rows);
    setHasGenerated(true);
  }, [rowCount, builtinIds, customFields]);

  const getOutput = () => {
    if (!generatedData.length) return "";
    if (outputFormat === "json") return JSON.stringify(generatedData, null, 2);
    if (outputFormat === "csv")  return toCSV(generatedData, allColumnIds);
    return "";
  };
  const output = getOutput();

  const saveHistory = useCallback((format, data) => {
    const entry = {
      id: Date.now(),
      rows: data.length,
      cols: allColumnIds.length,
      format,
      fields: allColumnIds.join(", ").slice(0, 80),
      data,
      timestamp: Date.now(),
    };
    setHistory(h => {
      const next = [entry, ...h].slice(0, 30);
      // Store without raw data to keep localStorage lean (data can be re-generated)
      const lean = next.map(({ data: _d, ...rest }) => rest);
      lsSet("mdt-fake-history", lean);
      return next;
    });
    onExport && onExport({ tool: "Fake Data", rows: data.length, cols: allColumnIds.length, timestamp: Date.now() });
  }, [allColumnIds, onExport]);

  const downloadJSON = () => {
    if (!hasGenerated) return;
    saveHistory("JSON", generatedData);
    const blob = new Blob([JSON.stringify(generatedData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "fake-data.json"; a.click(); URL.revokeObjectURL(url);
  };

  const downloadCSV = () => {
    if (!hasGenerated) return;
    saveHistory("CSV", generatedData);
    const csv = toCSV(generatedData, allColumnIds);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "fake-data.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const reDownload = (item) => {
    const content = item.format === "JSON"
      ? JSON.stringify(item.data, null, 2)
      : toCSV(item.data, Object.keys(item.data[0] || {}));
    const type = item.format === "JSON" ? "application/json" : "text/csv";
    const ext  = item.format === "JSON" ? "json" : "csv";
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `fake-data.${ext}`; a.click(); URL.revokeObjectURL(url);
  };

  const copyOutput = () =>
    navigator.clipboard.writeText(output).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); });

  const copyCell = val =>
    navigator.clipboard.writeText(String(val ?? "")).then(() => {
      setCopiedCell(String(val)); setTimeout(() => setCopiedCell(""), 1200);
    });

  const filteredGroups = FIELD_GROUPS.map(g => ({
    ...g,
    fields: g.fields.filter(f =>
      !groupSearch ||
      f.label.toLowerCase().includes(groupSearch.toLowerCase()) ||
      f.id.toLowerCase().includes(groupSearch.toLowerCase())
    ),
  })).filter(g => g.fields.length > 0);

  /* ── shared tab bar ── */
  const tabBarStyle = {
    display: "flex", gap: 4,
    borderBottom: `1px solid ${t.border}`,
    padding: "0 1.5rem",
    background: t.bgPanelHead,
  };
  const tabStyle = active => ({
    padding: "10px 16px", fontSize: 13,
    fontWeight: active ? 600 : 400,
    color: active ? t.textAccent : t.textSecondary,
    borderBottom: active ? `2px solid ${t.borderActive}` : "2px solid transparent",
    cursor: "pointer", background: "none", border: "none",
    fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "all 0.15s",
    display: "flex", alignItems: "center", gap: 6,
  });

  /* ── input helper ── */
  const inp = (val, onChange, placeholder, width) => (
    <input value={val} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 7, padding: "6px 10px", fontSize: 12, color: t.textPrimary, fontFamily: "JetBrains Mono, monospace", outline: "none", width: width || "100%", transition: "border-color 0.15s" }}
      onFocus={e => e.target.style.borderColor = t.borderActive}
      onBlur={e => e.target.style.borderColor = t.border}
    />
  );

  return (
    <div>
      {/* Tool Hero */}
      <div className="tool-hero fade-up">
        <div className="tool-hero-inner">
          <div className="tool-hero-left">
            <h2>⚗ Fake Data Generator</h2>
            <p>Generate realistic test data for forms, APIs, and databases. Add custom fields too.</p>
          </div>
          <div className="tool-hero-stats">
            <div className="tool-stat"><div className="tool-stat-val">{selectedFields.size + customFields.filter(f=>f.name.trim()).length}</div><div className="tool-stat-lbl">fields</div></div>
            <div className="tool-stat"><div className="tool-stat-val">{rowCount}</div><div className="tool-stat-lbl">rows</div></div>
            <div className="tool-stat"><div className="tool-stat-val">{history.length}</div><div className="tool-stat-lbl">exports</div></div>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={tabBarStyle}>
        {TABS.map(tab => (
          <button key={tab} style={tabStyle(activeTab === tab)} onClick={() => setActiveTab(tab)}>
            {tab === "History" && history.length > 0 && (
              <span style={{ background: t.borderActive, color:"#fff", borderRadius:10, padding:"1px 6px", fontSize:10 }}>
                {history.length}
              </span>
            )}
            {tab}
          </button>
        ))}
      </div>

      {/* ── GENERATOR TAB ── */}
      {activeTab === "Generator" && (
        <div className="tool-main">
          <div className="grid-2" style={{ marginBottom:"1rem", alignItems:"start" }}>

            {/* Left: field picker */}
            <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>

              {/* Built-in fields */}
              <div className="panel d1 fade-up">
                <div className="panel-header">
                  <div className="panel-title"><div className="dot dot-blue" />Built-in Fields <span style={{ color:t.textMuted, fontSize:10 }}>{selectedFields.size} selected</span></div>
                  <div className="panel-action">
                    <button className="btn-sm" onClick={() => setSelectedFields(new Set(ALL_FIELDS.map(f=>f.id)))}>All</button>
                    <button className="btn-sm" onClick={() => setSelectedFields(new Set(DEFAULT_FIELDS))}>Reset</button>
                  </div>
                </div>
                <div style={{ padding:"8px 14px", borderBottom:`1px solid ${t.border}`, background:t.bgPanelHead }}>
                  <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)}
                    placeholder="Search fields…"
                    style={{ width:"100%", background:t.bgSearchInp, border:`1px solid ${t.border}`, borderRadius:7, padding:"5px 10px", fontSize:12, color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", outline:"none" }} />
                </div>
                <div style={{ maxHeight:350, overflowY:"auto", padding:"10px 14px", display:"flex", flexDirection:"column", gap:12, scrollbarWidth:"thin" }}>
                  {filteredGroups.map(group => (
                    <div key={group.label}>
                      <div style={{ fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textMuted, marginBottom:6 }}>{group.label}</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                        {group.fields.map(f => {
                          const active = selectedFields.has(f.id);
                          return (
                            <div key={f.id} className={`chip ${active ? "active" : ""}`} onClick={() => toggleField(f.id)} style={{ fontSize:11 }}>
                              {active && <div className="check">✓</div>}
                              <span style={{ marginRight:2 }}>{f.icon}</span>{f.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Custom Fields ── */}
              <div className="panel d2 fade-up">
                <div className="panel-header" style={{ cursor:"pointer" }} onClick={() => setCustomOpen(v => !v)}>
                  <div className="panel-title">
                    <div className="dot dot-amber" />
                    Custom Fields
                    {customFields.length > 0 && (
                      <span style={{ background:t.borderActive, color:"#fff", borderRadius:10, padding:"1px 6px", fontSize:10 }}>
                        {customFields.length}
                      </span>
                    )}
                  </div>
                  <span style={{ color:t.chevron, fontSize:12, display:"inline-block", transition:"transform 0.25s", transform: customOpen ? "rotate(180deg)" : "none" }}>▾</span>
                </div>

                {customOpen && (
                  <div className="slide-down" style={{ padding:"12px 14px 14px" }}>

                    {/* Existing custom fields list */}
                    {customFields.length > 0 && (
                      <div style={{ marginBottom:12, display:"flex", flexDirection:"column", gap:6 }}>
                        {customFields.map(cf => (
                          <div key={cf.id} style={{ display:"flex", alignItems:"center", gap:8, background:t.bgChipActive, borderRadius:8, padding:"7px 10px" }}>
                            <span style={{ fontSize:13 }}>✏</span>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:12, fontWeight:600, color:t.textPrimary, fontFamily:"JetBrains Mono" }}>{cf.name}</div>
                              <div style={{ fontSize:10, color:t.textMuted }}>
                                {CUSTOM_TYPES.find(x => x.id === cf.type)?.label}
                                {cf.type === "static"    && cf.value  ? ` — "${cf.value}"` : ""}
                                {cf.type === "list"      && cf.value  ? ` — [${cf.value.split(",").length} options]` : ""}
                                {cf.type === "number"                 ? ` — ${cf.min}–${cf.max}` : ""}
                                {cf.type === "date"                   ? ` — ${cf.startYear}–${cf.endYear}` : ""}
                              </div>
                            </div>
                            <button className="btn-sm" onClick={() => removeCustomField(cf.id)}
                              style={{ color:t.statusErr, borderColor:t.statusErr, flexShrink:0 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add new custom field form */}
                    <div style={{ background:t.bgPanel, border:`1px solid ${t.border}`, borderRadius:10, padding:"12px" }}>
                      <div style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.07em", color:t.textMuted, marginBottom:10 }}>
                        Add New Custom Field
                      </div>

                      {/* Name + type row */}
                      <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap" }}>
                        <div style={{ flex:1, minWidth:120 }}>
                          <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>Field Name</div>
                          {inp(newFieldName, setNewFieldName, "e.g. region, tier, tag…")}
                        </div>
                        <div style={{ flex:1, minWidth:140 }}>
                          <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>Value Type</div>
                          <select value={newFieldType} onChange={e => setNewFieldType(e.target.value)}
                            style={{ width:"100%", background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:7, padding:"6px 10px", fontSize:12, color:t.textAccent, fontFamily:"JetBrains Mono, monospace", outline:"none", cursor:"pointer" }}>
                            {CUSTOM_TYPES.map(ct => <option key={ct.id} value={ct.id}>{ct.label}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Value config based on type */}
                      {newFieldType === "static" && (
                        <div style={{ marginBottom:10 }}>
                          <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>Static Value</div>
                          {inp(newFieldValue, setNewFieldValue, "e.g. active, US, tier-1…")}
                        </div>
                      )}
                      {newFieldType === "list" && (
                        <div style={{ marginBottom:10 }}>
                          <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>Options (comma-separated)</div>
                          {inp(newFieldValue, setNewFieldValue, "e.g. bronze, silver, gold")}
                        </div>
                      )}
                      {newFieldType === "number" && (
                        <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>Min</div>
                            {inp(newFieldMin, setNewFieldMin, "0", "100%")}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>Max</div>
                            {inp(newFieldMax, setNewFieldMax, "100", "100%")}
                          </div>
                        </div>
                      )}
                      {newFieldType === "date" && (
                        <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>Start Year</div>
                            {inp(newFieldStartYear, setNewFieldStartYear, "2020", "100%")}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>End Year</div>
                            {inp(newFieldEndYear, setNewFieldEndYear, "2024", "100%")}
                          </div>
                        </div>
                      )}

                      {/* hint */}
                      <div style={{ fontSize:11, color:t.textMuted, marginBottom:10, fontStyle:"italic" }}>
                        {CUSTOM_TYPES.find(x => x.id === newFieldType)?.hint}
                      </div>

                      <button className="btn-sm" onClick={addCustomField}
                        style={{ borderColor: newFieldName.trim() ? t.borderActive : t.border, color: newFieldName.trim() ? t.textAccent : t.textMuted, padding:"5px 16px" }}>
                        + Add Field
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: settings + field preview */}
            <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>

              {/* Generate settings */}
              <div className="panel d3 fade-up">
                <div className="panel-header">
                  <div className="panel-title"><div className="dot dot-green" />Generate Settings</div>
                </div>
                <div style={{ padding:"16px 14px", display:"flex", flexDirection:"column", gap:14 }}>
                  <div>
                    <div style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.07em", color:t.textMuted, marginBottom:8 }}>Number of Rows</div>
                    <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                      <input type="number" min={1} max={1000} value={rowCount}
                        onChange={e => setRowCount(Math.min(1000, Math.max(1, parseInt(e.target.value)||1)))}
                        style={{ width:90, background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:7, padding:"6px 10px", fontSize:13, color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", outline:"none" }} />
                      <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                        {[10,25,50,100,500].map(n => (
                          <button key={n} className="btn-sm" onClick={() => setRowCount(n)}
                            style={{ borderColor: rowCount===n ? t.borderActive : t.border, color: rowCount===n ? t.textAccent : t.textMuted, background: rowCount===n ? t.bgChipActive : "none" }}>
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.07em", color:t.textMuted, marginBottom:8 }}>Preview Format</div>
                    <div style={{ display:"flex", gap:6 }}>
                      {["table","json","csv"].map(fmt => (
                        <button key={fmt} className="btn-sm" onClick={() => setOutputFormat(fmt)}
                          style={{ borderColor: outputFormat===fmt ? t.borderActive : t.border, color: outputFormat===fmt ? t.textAccent : t.textMuted, background: outputFormat===fmt ? t.bgChipActive : "none", padding:"4px 14px" }}>
                          {fmt.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button className="btn-primary" onClick={generate}
                    style={{ width:"100%", justifyContent:"center", padding:12 }}>
                    ⚗ Generate {rowCount} Row{rowCount !== 1 ? "s" : ""}
                  </button>
                </div>
              </div>

              {/* Column preview */}
              <div className="panel d4 fade-up">
                <div className="panel-header">
                  <div className="panel-title"><div className="dot dot-amber" />All Columns ({allColumnIds.length})</div>
                </div>
                <div style={{ padding:"10px 14px", display:"flex", flexWrap:"wrap", gap:5 }}>
                  {allColumnIds.length === 0
                    ? <span style={{ fontSize:12, color:t.textMuted }}>No fields selected</span>
                    : allColumnIds.map(id => {
                        const f = ALL_FIELDS.find(x => x.id === id);
                        const isCustom = customFields.some(cf => cf.name.trim() === id);
                        return (
                          <span key={id} className={`pill ${isCustom ? "pill-amber" : "pill-blue"}`} style={{ fontSize:10 }}>
                            {f ? `${f.icon} ${f.label}` : `✏ ${id}`}
                          </span>
                        );
                      })
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Output area */}
          {hasGenerated && (
            <div ref={outputRef} className="panel d5 fade-up" style={{ marginBottom:"1rem" }}>
              <div className="panel-header">
                <div className="panel-title">
                  <div className="dot dot-green" />
                  Generated Data
                  <span className="pill pill-green" style={{ marginLeft:4 }}>{generatedData.length} rows</span>
                  {customFields.filter(f=>f.name.trim()).length > 0 && (
                    <span className="pill pill-amber" style={{ marginLeft:4 }}>+{customFields.filter(f=>f.name.trim()).length} custom</span>
                  )}
                </div>
                <div className="panel-action">
                  <button className="btn-sm" onClick={generate}>↻ Regenerate</button>
                </div>
              </div>

              {outputFormat === "table" && (
                <>
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          {allColumnIds.map(id => {
                            const f = ALL_FIELDS.find(x => x.id === id);
                            const isCustom = customFields.some(cf => cf.name.trim() === id);
                            return (
                              <th key={id} style={{ background: isCustom ? t.bgChipActive : t.thBg }}>
                                {f ? `${f.icon} ${f.label}` : `✏ ${id}`}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {generatedData.map((row, i) => (
                          <tr key={i}>
                            {allColumnIds.map(id => (
                              <td key={id} onClick={() => copyCell(row[id])} title="Click to copy" style={{ cursor:"copy" }}>
                                {copiedCell === String(row[id])
                                  ? <span style={{ color:t.statusOk }}>✓</span>
                                  : String(row[id] ?? "")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="status-bar ok">
                    <span style={{ color:t.rowStatsVal }}>{generatedData.length}</span> rows ·{" "}
                    <span style={{ color:t.rowStatsVal }}>{allColumnIds.length}</span> columns · Click any cell to copy
                  </div>
                </>
              )}

              {(outputFormat === "json" || outputFormat === "csv") && (
                <>
                  <textarea readOnly value={output}
                    style={{ width:"100%", height:300, background:"transparent", border:"none", outline:"none", padding:14, color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", fontSize:11, lineHeight:1.7, resize:"vertical" }}
                  />
                  <div className="status-bar ok">
                    {outputFormat === "json" ? "JSON array ready" : "CSV format ready"} · {generatedData.length} rows · {allColumnIds.length} columns
                  </div>
                </>
              )}
            </div>
          )}

          {/* Download bar */}
          <div className="d6 fade-up" style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
            <button className="btn-green" onClick={downloadJSON} disabled={!hasGenerated}>↓ Download JSON</button>
            <button className="btn-primary" onClick={downloadCSV} disabled={!hasGenerated}>↓ Download CSV</button>
            {(outputFormat === "json" || outputFormat === "csv") && (
              <button className={`btn-secondary ${copied ? "success" : ""}`} onClick={copyOutput} disabled={!hasGenerated || !output}>
                {copied ? "✓ Copied!" : "⎘ Copy Output"}
              </button>
            )}
            {!hasGenerated && (
              <span style={{ fontSize:12, color:t.textMuted, fontFamily:"JetBrains Mono" }}>
                Click "Generate" to create your data first
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {activeTab === "History" && (
        <div className="tool-main">
          {history.length === 0 ? (
            <div style={{ textAlign:"center", padding:"4rem 1rem", color:t.textMuted }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🧪</div>
              <div style={{ fontSize:14, marginBottom:6 }}>No exports yet this session</div>
              <div style={{ fontSize:12 }}>Every JSON or CSV download will appear here</div>
              <button className="btn-sm" style={{ marginTop:16 }} onClick={() => setActiveTab("Generator")}>
                ← Back to Generator
              </button>
            </div>
          ) : (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem" }}>
                <div style={{ fontSize:13, color:t.textSecondary }}>
                  <span style={{ color:t.textAccent, fontWeight:600 }}>{history.length}</span> export{history.length !== 1 ? "s" : ""} this session
                </div>
                <button className="btn-sm" onClick={() => { setHistory([]); lsSet("mdt-fake-history", []); }}>Clear All</button>
              </div>
              <div style={{ display:"grid", gap:8 }}>
                {history.map(item => (
                  <div key={item.id} className="panel"
                    style={{ padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:8, background: item.format === "JSON" ? t.pillAmber : t.bgChipActive, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>
                      {item.format === "JSON" ? "📦" : "📄"}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:500, color:t.textPrimary, marginBottom:3, display:"flex", alignItems:"center", gap:6 }}>
                        fake-data.{item.format.toLowerCase()}
                        <span className={`pill ${item.format === "JSON" ? "pill-amber" : "pill-blue"}`} style={{ fontSize:9 }}>{item.format}</span>
                      </div>
                      <div style={{ fontSize:11, color:t.textMuted, fontFamily:"JetBrains Mono", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                        {item.rows} rows · {item.cols} cols · {formatTime(item.timestamp)} · {item.fields}…
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                      <button className="btn-sm" onClick={() => reDownload(item)}>↓ Re-download</button>
                      <button className="btn-sm" onClick={() => setHistory(h => { const next = h.filter(x => x.id !== item.id); lsSet("mdt-fake-history", next.map(({data:_d,...r})=>r)); return next; })}
                        style={{ color:t.statusErr, borderColor:t.statusErr }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <article className="tool-seo-section" aria-label="About Fake Data Generator">
        <h2>About Fake Data Generator</h2>
        <div className="tool-seo-cols">
          <div>
            <h3>How it works</h3>
            <ul>
              <li>Select from 40+ built-in fields across 7 categories: Identity, Contact, Address, Work, System, Financial, and Misc.</li>
              <li>Add custom fields with static values, lists, number ranges, booleans, incrementing numbers, UUIDs, or date ranges.</li>
              <li>Preview data instantly in table, JSON, or CSV format.</li>
              <li>Export up to 1,000 rows as JSON or CSV with a single click.</li>
            </ul>
          </div>
          <div>
            <h3>Use cases</h3>
            <ul>
              <li>Populate test databases with realistic-looking records.</li>
              <li>Fill UI prototypes and mockups with varied data.</li>
              <li>Generate CSV datasets for testing import pipelines.</li>
              <li>Create realistic API response fixtures for frontend development.</li>
            </ul>
          </div>
          <div>
            <h3>Privacy</h3>
            <ul>
              <li>All data generation runs locally in your browser.</li>
              <li>No generated data is sent to any server.</li>
              <li>Free to use with no sign-up required.</li>
            </ul>
          </div>
        </div>
        <h2 style={{ marginTop:"1.5rem" }}>Frequently Asked Questions</h2>
        <details><summary>Can I add my own custom fields?</summary><p>Yes. Click "Add Custom Field" and name your field. Choose a value type: static, comma-separated list, number range, boolean, incrementing counter, UUID, or date range. Custom fields appear alongside built-in fields in the preview and export.</p></details>
        <details><summary>What is the maximum number of rows I can generate?</summary><p>The generator supports up to 1,000 rows per export. For large datasets, export in batches. The preview table shows the first 50 rows to keep the interface responsive.</p></details>
        <details><summary>Can I export as both JSON and CSV?</summary><p>Yes. Use the JSON Export button for a JSON array and the CSV Export button for a comma-separated file. Both exports include all selected fields and all generated rows.</p></details>
        <details><summary>Is the generated data sent to any server?</summary><p>No. All data generation happens locally in your browser using JavaScript. No generated data — including names, emails, addresses, or credit card numbers — is ever transmitted to any server. You can verify this by checking the Network tab in DevTools.</p></details>
        <h2 style={{ marginTop:"1.5rem" }}>Alternatives &amp; why miniDevToolkit</h2>
        <p>Compared to <strong>Mockaroo</strong> — miniDevToolkit generates data entirely in your browser with no server uploads, no account, and no 1,000-row free-tier limit per day. Compared to <strong>Faker.js</strong> — miniDevToolkit is a GUI tool requiring no code or npm install. Compared to <strong>generatedata.com</strong> — miniDevToolkit is entirely client-side (no file uploads), includes custom fields, and requires no registration. All generation is instant and private.</p>
      </article>
    </div>
  );
}